export type DashboardNavIcon =
  | { kind: 'material'; name: string }
  | { kind: 'image'; src: string; alt: string };

export type DashboardNavLeaf = {
  id: string;
  label: string;
  to: string;
  match: string | string[];
  exact?: boolean;
  icon: DashboardNavIcon;
  adminOnly?: boolean;
  mobileOnly?: boolean;
  action?: 'logout';
};

export type DashboardNavGroup = {
  id: string;
  label: string;
  match: string | string[];
  icon: DashboardNavIcon;
  adminOnly?: boolean;
  children: DashboardNavLeaf[];
  /** When true, first open also navigates to this path */
  navigateOnExpand?: string;
};

export type DashboardNavItem = DashboardNavLeaf | DashboardNavGroup;

export function isNavGroup(item: DashboardNavItem): item is DashboardNavGroup {
  return Array.isArray((item as DashboardNavGroup).children);
}

export function pathMatches(
  pathname: string,
  match: string | string[],
  exact = false,
): boolean {
  const patterns = Array.isArray(match) ? match : [match];
  return patterns.some(pattern =>
    exact ? pathname === pattern : pathname.includes(pattern),
  );
}

export const DASHBOARD_NAV_ITEMS: DashboardNavItem[] = [
  {
    id: 'home',
    label: 'Home',
    to: '/u/dashboard',
    match: '/u/dashboard',
    exact: true,
    icon: { kind: 'material', name: 'home' },
  },
  {
    id: 'share',
    label: 'Share QuickCam',
    to: '/u/share',
    match: '/share',
    icon: { kind: 'material', name: 'share' },
  },
  {
    id: 'quick-links',
    label: 'QuickLinks',
    to: '/u/quicklinks',
    match: '/u/quicklinks',
    icon: { kind: 'material', name: 'link' },
  },
  {
    id: 'connections',
    label: 'Connections',
    match: 'connections',
    icon: { kind: 'material', name: 'people' },
    children: [
      {
        id: 'referrals',
        label: 'Referrals',
        to: '/u/connections/referrals',
        match: '/connections/referrals',
        icon: { kind: 'material', name: 'group_add' },
      },
      {
        id: 'referrers',
        label: 'Referred by',
        to: '/u/connections/referrers',
        match: '/connections/referrers',
        icon: { kind: 'material', name: 'record_voice_over' },
      },
      {
        id: 'favorites',
        label: 'Favorites',
        to: '/u/connections/favorites',
        match: '/connections/favorites',
        icon: { kind: 'material', name: 'favorite_border' },
      },
      {
        id: 'following',
        label: 'Following',
        to: '/u/connections/following',
        match: '/connections/following',
        icon: { kind: 'material', name: 'person_add' },
      },
      {
        id: 'followers',
        label: 'Followers',
        to: '/u/connections/followers',
        match: '/connections/followers',
        icon: { kind: 'material', name: 'group' },
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    match: ['/admin', '/sizzle-video'],
    icon: { kind: 'material', name: 'dashboard' },
    adminOnly: true,
    children: [
      {
        id: 'admin-dashboard',
        label: 'QuickCam Dashboard',
        to: '/u/admin/dashboard',
        match: '/admin/dashboard',
        icon: { kind: 'material', name: 'speed' },
        adminOnly: true,
      },
      {
        id: 'admin-quick-links',
        label: 'QuickLinks',
        to: '/u/admin/quick-links',
        match: '/admin/quick-links',
        icon: { kind: 'material', name: 'link' },
        adminOnly: true,
      },
      {
        id: 'admin-creator-upgrades',
        label: 'Creator Upgrades',
        to: '/u/admin/creator-upgrades',
        match: '/admin/creator-upgrades',
        icon: { kind: 'material', name: 'star' },
        adminOnly: true,
      },
      {
        id: 'admin-announcements',
        label: 'Announcements',
        to: '/u/admin/announcements',
        match: '/admin/announcements',
        icon: { kind: 'material', name: 'campaign' },
        adminOnly: true,
      },
      {
        id: 'admin-sizzle',
        label: 'Sizzle Video',
        to: '/u/sizzle-video',
        match: '/sizzle-video',
        icon: { kind: 'material', name: 'whatshot' },
        adminOnly: true,
      },
      {
        id: 'admin-credits',
        label: 'Credits Pricing',
        to: '/u/admin/quickcam-credits',
        match: '/admin/quickcam-credits',
        icon: { kind: 'material', name: 'price_change' },
        adminOnly: true,
      },
    ],
  },
  {
    id: 'credits',
    label: 'QuickCam Credits',
    to: '/u/quickcam-credits',
    match: '/u/quickcam-credits',
    icon: { kind: 'material', name: 'toll' },
  },
  {
    id: 'lifetime-credits',
    label: 'LA Pass Gift Cards',
    to: '/u/lifetime-credits',
    match: '/lifetime-credits',
    icon: { kind: 'material', name: 'card_giftcard' },
  },
  {
    id: 'creator-tools',
    label: 'Creator Tools',
    match: '/creator-upgrades',
    icon: { kind: 'material', name: 'star' },
    navigateOnExpand: '/u/creator-upgrades/overview',
    children: [
      {
        id: 'creator-overview',
        label: 'Overview',
        to: '/u/creator-upgrades/overview',
        match: '/creator-upgrades/overview',
        icon: { kind: 'material', name: 'dashboard' },
      },
      {
        id: 'creator-subscriptions',
        label: 'Subscriptions',
        to: '/u/creator-upgrades/subscriptions',
        match: '/creator-upgrades/subscriptions',
        icon: { kind: 'material', name: 'inventory' },
      },
      {
        id: 'creator-purchase-history',
        label: 'Purchase History',
        to: '/u/creator-upgrades/purchase-history',
        match: '/creator-upgrades/purchase-history',
        icon: { kind: 'material', name: 'history' },
      },
    ],
  },
  {
    id: 'commissions',
    label: 'Commissions',
    to: '/u/commissions',
    match: '/commissions',
    icon: { kind: 'material', name: 'payment' },
  },
  {
    id: 'leaderboard',
    label: 'Leaderboard',
    to: '/u/leaderboard',
    match: '/leaderboard',
    icon: { kind: 'material', name: 'leaderboard' },
  },
  {
    id: 'notifications',
    label: 'Notifications',
    to: '/u/notification',
    match: '/notification',
    icon: { kind: 'material', name: 'notifications' },
  },
  {
    id: 'announcements',
    label: 'Announcements',
    to: '/u/announcements',
    match: '/announcements',
    icon: { kind: 'material', name: 'campaign' },
  },
  {
    id: 'downloads',
    label: 'Get QuickCam',
    to: '/u/downloads',
    match: '/downloads',
    icon: { kind: 'material', name: 'file_download' },
  },
  {
    id: 'help',
    label: 'Help',
    match: '/help',
    icon: { kind: 'material', name: 'help' },
    children: [
      {
        id: 'faqs',
        label: 'FAQs',
        to: '/u/help/faqs',
        match: '/help/faqs',
        icon: { kind: 'material', name: 'quiz' },
      },
      {
        id: 'tutorials',
        label: 'Tutorials',
        to: '/u/help/tutorials',
        match: '/help/tutorials',
        icon: { kind: 'material', name: 'home_repair_service' },
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    to: '/u/settings',
    match: '/settings',
    icon: { kind: 'material', name: 'settings' },
  },
  {
    id: 'logout',
    label: 'Logout',
    to: '',
    match: '',
    icon: { kind: 'material', name: 'logout' },
    mobileOnly: true,
    action: 'logout',
  },
];
